import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";

export type PaymentReferenceDocument = {
  file: File;
  documentId: number;
};

function base64ToPdfFile(base64: string, fileName?: string | null): File | null {
  try {
    const cleaned = base64.includes(",") ? base64.split(",")[1] : base64;
    if (!cleaned?.trim()) return null;
    const byteCharacters = atob(cleaned);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    return new File([blob], fileName || "invoice.pdf", { type: "application/pdf" });
  } catch (error) {
    console.error("Failed to decode PaymentReference base64 PDF", error);
    return null;
  }
}

/** Load a PaymentReference PDF for a single payment due-account id. */
export async function fetchPaymentReferenceDocument(
  paymentId: number,
): Promise<PaymentReferenceDocument | null> {
  if (!paymentId || paymentId <= 0) return null;

  try {
    const res = await apiHelper(
      DocumentService.getApiDocumentBase64,
      { showNotification: false },
      paymentId,
      DocumentEntityTypesEnum.Payment,
      DocumentTypesEnum.PaymentReference,
    );

    const items = res?.data?.success;
    if (!items?.length) return null;

    const doc = items[0];
    if (!doc?.id || !doc.base64) return null;

    const file = base64ToPdfFile(doc.base64, doc.fileName);
    if (!file) return null;

    return { file, documentId: doc.id };
  } catch (error) {
    console.error("Failed to fetch PaymentReference document", error);
    return null;
  }
}

/**
 * Prefer the payment the user opened, then walk invoice line items until a
 * PaymentReference document is found (covers multi-line invoices).
 */
export async function fetchFirstPaymentReferenceDocument(
  paymentIds: readonly (number | null | undefined)[],
  preferredPaymentId?: number | null,
): Promise<PaymentReferenceDocument | null> {
  const ordered: number[] = [];
  const seen = new Set<number>();

  const push = (id?: number | null) => {
    if (!id || id <= 0 || seen.has(id)) return;
    seen.add(id);
    ordered.push(id);
  };

  push(preferredPaymentId);
  for (const id of paymentIds) push(id);

  for (const id of ordered) {
    const found = await fetchPaymentReferenceDocument(id);
    if (found) return found;
  }

  return null;
}

/** Upload PaymentReference using the File itself (preserves name + multipart). */
export async function uploadPaymentReferenceDocument(
  paymentId: number,
  file: File,
): Promise<boolean> {
  if (!paymentId || paymentId <= 0 || !file) return false;

  const res = await apiHelper(
    DocumentService.postApiDocument,
    { showNotification: false },
    {
      EntityTypeId: DocumentEntityTypesEnum.Payment,
      EntityId: paymentId,
      DocumentTypeId: DocumentTypesEnum.PaymentReference,
      IsMain: true,
      FileName: file.name,
      Document: file,
    },
  );

  return Boolean(res?.data?.success);
}
